import { EchartsWithTheme, useTheme } from '@maxtropy/components';
import dayjs, { Dayjs } from 'dayjs';
import { isNil } from 'lodash-es';
import { useRef, useMemo, useEffect, useContext } from 'react';
import { EchartsWithThemeRef } from '@maxtropy/components/es/components/EchartsWithTheme';
import { keepTwoNull } from '@/utils/utils';
import { ShareDataWrapperContext } from '@/pages/WgqAllMaps';
interface ChartData {
  time: number;
  totalAmount: number;
  longFlowAmount: number | null;
  nonLongFlowAmount: number | null;
  longFlowProportion?: number;
  nonLongFlowProportion?: number;
  pressure?: number;
  flag?: boolean;
}
const getTimeAxis = (time: number) => {
  return dayjs(time).format('YYYY-MM-DD HH:mm');
};
export const COLORS = {
  longFlow: '#FF9247',
  longFlowLight: '#F58F65',
  nonLongFlow: '#00ADFF',
  nonLongFlowLight: '#32B2EE',
  trendLine: '#FFCB47',
  trendLineLight: '#FFD34F',
  pressure: '#16DD8E',
  pressureLight: '#49CC98',
  longFlowBg: 'rgba(255,146,71,0.1)',
  nonLongFlowBg: 'rgba(0,173,255,0.1)',
};
const lineLegendIcon = 'path://M0,0 L12,0 L12,4 L0,4 Z';
const bgLegendIcon = 'path://M0,0 L10,0 L10,12 L0,12 Z';
const formatLegendName = (name: string) => (name.length > 5 ? `${name.slice(0, 4)}...` : name);

// 构建基于时间戳的 markArea 数据
const buildTimeBasedAreas = (chartData: ChartData[]) => {
  const longAreas: any[] = [];
  const nonLongAreas: any[] = [];

  if (chartData.length === 0) return { longAreas, nonLongAreas };

  // 计算采样间隔（假设数据基本等间隔，取前两个点的差值）
  let interval = 3600000; // 默认 1 小时（毫秒），避免除零
  if (chartData.length > 1) {
    interval = chartData[1].time - chartData[0].time;
  }
  const halfInterval = interval / 2;

  let start = 0;
  let curr = chartData[0].flag;

  for (let i = 1; i < chartData.length; i++) {
    if (chartData[i].flag !== curr) {
      const from = chartData[start].time - halfInterval;
      const to = chartData[i - 1].time + halfInterval;
      if (curr) longAreas.push([{ xAxis: from }, { xAxis: to }]);
      else nonLongAreas.push([{ xAxis: from }, { xAxis: to }]);
      start = i;
      curr = chartData[i].flag;
    }
  }

  // 最后一个区域
  const from = chartData[start].time - halfInterval;
  const to = chartData[chartData.length - 1].time + halfInterval;
  if (curr) longAreas.push([{ xAxis: from }, { xAxis: to }]);
  else nonLongAreas.push([{ xAxis: from }, { xAxis: to }]);

  return { longAreas, nonLongAreas };
};

const hourTooltipFormatter = {
  trigger: 'axis',
  formatter: (params: any) => {
    const raw = params[0]?.data[2];
    const dateLabel = params[0]?.axisValue ?? '';
    let result = `<div style="color:rgba(255,255,255,0.85);margin-bottom:8px">${dayjs(dateLabel).format(
      'YYYY-MM-DD HH:mm'
    )}</div>`;

    result += `<div style="display:flex;flex-direction: column;gap:8px;">
              <div><span style="color:rgba(255,255,255,0.85)">用量：${keepTwoNull(raw?.totalAmount)}m³</span></div>
              <div><span style="color:rgba(255,255,255,0.85)">压强：${keepTwoNull(raw?.pressure)}kPa</span></div>
            </div>`;
    return result;
  },
};

const HourChartOption = (chartData: ChartData[], pageLabel?: string, showDataZoom: boolean = true) => {
  const { longAreas, nonLongAreas } = buildTimeBasedAreas(chartData);

  return {
    backgroundColor: 'transparent',
    grid: {
      left: 20,
      right: 20,
      top: 70,
      bottom: showDataZoom ? 50 : 16,
      containLabel: true,
    },
    legend: {
      top: 10,
      right: 0,
      width: 408,
      orient: 'horizontal',
      itemWidth: 10,
      itemHeight: 10,
      itemGap: 6,
      formatter: formatLegendName,
      textStyle: {
        overflow: 'truncate',
        ellipsis: '...',
      },
      data: [
        { name: `非${pageLabel}监测时段`, icon: bgLegendIcon },
        { name: `${pageLabel}监测时段`, icon: bgLegendIcon },
        { name: `非${pageLabel}用量`, icon: 'rect' },
        { name: `${pageLabel}用量`, icon: 'rect' },
        { name: '压强', icon: lineLegendIcon },
      ],
    },
    tooltip: hourTooltipFormatter,
    yAxis: [
      {
        type: 'value',
        name: 'm³',
        alignTicks: true,
        axisLabel: { formatter: '{value}' },
      },
      {
        type: 'value',
        name: 'kPa',
        alignTicks: true,
        axisLabel: { formatter: '{value}' },
      },
    ],
    xAxis: {
      type: 'time',
      axisLabel: {
        formatter: (value: number) => {
          const date = dayjs(value);
          return date.format('MM-DD HH:mm');
        },
        rotate: 45,
        interval: 'auto',
      },
    },
    series: [
      {
        type: 'bar',
        name: `${pageLabel}用量`,
        stack: 'total',
        barMaxWidth: 20,
        barMinWidth: 4,
        data: chartData.map(d => [d.time, d.longFlowAmount, d]),
        color: COLORS.longFlow,
      },
      {
        type: 'line',
        name: `${pageLabel}监测时段`,
        barMinWidth: 0,
        color: COLORS.longFlowBg,
        markArea: {
          silent: true,
          itemStyle: { color: COLORS.longFlowBg },
          data: longAreas,
        },
      },
      {
        type: 'bar',
        name: `非${pageLabel}用量`,
        stack: 'total',
        barMaxWidth: 20,
        barMinWidth: 4,
        data: chartData.map(d => [d.time, d.nonLongFlowAmount, d]),
        color: COLORS.nonLongFlow,
        markArea: {
          silent: true,
          itemStyle: { color: COLORS.nonLongFlowBg },
          data: nonLongAreas,
        },
      },
      {
        type: 'line',
        name: `非${pageLabel}监测时段`,
        barMinWidth: 0,
        color: COLORS.nonLongFlowBg,
        markArea: {
          silent: true,
          itemStyle: { color: COLORS.nonLongFlowBg },
          data: nonLongAreas,
        },
      },
      {
        type: 'line',
        name: '压强',
        yAxisIndex: 1,
        data: chartData.map(d => [d.time, d.pressure, d]),
        smooth: true,
        color: COLORS.pressure,
        lineStyle: { width: 2 },
        showSymbol: false,
      },
    ],
  };
};

const getChartOption = (chartData: ChartData[], pageLabel?: string, showDataZoom: boolean = true) => {
  return {
    ...(showDataZoom
      ? {
          dataZoom: [
            {
              type: 'slider',
              left: 50,
              right: 10,
              height: 20,
              textStyle: { fontSize: 10 },
            },
          ],
        }
      : {}),
    ...HourChartOption(chartData, pageLabel, showDataZoom),
  };
};

interface ChartProps {
  pageLabel?: string;
  chartData: ChartData[];
  queryTimeRanges?: [Dayjs | null, Dayjs | null];
  height?: number;
}

const Chart = ({ pageLabel, chartData, queryTimeRanges, height = 460 }: ChartProps) => {
  const theme = useTheme();
  const { isStandardSize } = useContext(ShareDataWrapperContext);
  const chartRef = useRef<EchartsWithThemeRef>();
  const showDataZoom = isStandardSize;
  const options = useMemo(
    () => getChartOption(chartData, pageLabel, showDataZoom),
    [chartData, pageLabel, queryTimeRanges, showDataZoom]
  );

  useEffect(() => {
    const handleResize = () => {
      chartRef.current?.getInstance()?.resize();
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <EchartsWithTheme
      theme={theme}
      ref={d => {
        if (d) chartRef.current = d;
      }}
      option={options}
      onEvents={{
        brushSelected: (params: any) => {
          var brushed: any[] = [];
          let startTime;
          let endTime;
          var brushComponent = params.batch[0];
          const chartObject = chartRef.current?.getInstance();
          const series = chartObject?.getOption()?.series as any[];

          brushComponent.selected.forEach((item: any, index: number) => {
            const findSeries = series?.find(i => i.name === item.seriesName);
            if (!findSeries) return;

            if (index !== 0) return; // 小时视图只统计第一个系列（累积流量）

            let arr: any[] = [];
            let totalArr: any[] = [];

            item.dataIndex.forEach((c: number, idx: number) => {
              const dataPoint = findSeries.data[c];
              let time, value, raw;

              // 时间轴二维数组
              time = dataPoint[0];
              value = dataPoint[1];
              raw = dataPoint[2];

              if (idx === 0) {
                startTime = dayjs(time).format('YYYY-MM-DD HH:mm');
              }
              if (idx === item.dataIndex.length - 1) {
                endTime = dayjs(time).format('YYYY-MM-DD HH:mm');
              }
              arr.push(value ?? 0);
              totalArr.push(raw?.totalAmount ?? 0);
            });

            if (arr.filter(x => !isNil(x)).length > 0) {
              const totalArrData = totalArr.filter(x => !isNil(x)).reduce((a, b) => a + b, 0);
              brushed.push('累积流量（累计）：' + (!isNil(totalArrData) ? totalArrData.toFixed(2) : '--') + 'm³');
            }
          });

          if (brushComponent.areas.length > 0) {
            chartObject?.setOption({
              title: {
                show: true,
                padding: 8,
                backgroundColor: 'rgba(0,0,0,0.8)',
                borderColor: 'transparent',
                borderRadius: 2,
                textStyle: { color: 'rgba(255,255,255,0.85)', fontSize: 12, fontWeight: 500 },
                text: startTime + ' ~ ' + endTime + '\n\n' + brushed.join('\n\n'),
                top: 100,
                left: brushComponent.areas[0].range[1] - 240,
              },
            });
          } else {
            chartObject?.setOption({ title: { show: false } });
          }
        },
      }}
      empty={{ isShowEmpty: chartData.length === 0 }}
      style={{ height, width: '100%', paddingBottom: 24 }}
    />
  );
};

export default Chart;
